// Engine selection. This is the public face of the seam (plan §3): every caller
// outside src/engine/ imports startEngine from here and never learns which
// implementation ran.
//
//   · WebXR (`immersive-ar`)  — native ARCore tracking on Android Chrome.
//     Preferred wherever the browser reports support: better tracking than a
//     camera-pipeline SLAM, and it skips a 6.3 MB engine download.
//   · 8th Wall engine binary  — camera-pipeline SLAM. The ONLY option on iOS
//     Safari (no WebXR AR there), and the fallback on any Android device without
//     ARCore or without a local-floor reference space.
//
// No UA sniffing: iOS has no navigator.xr, so it lands on the 8th Wall path by
// the same rule that puts a modern Android phone on WebXR.
import type {EngineCallbacks, EngineKind, EngineSession} from './types'
import type {WebXrSupport} from './webxrEngine'
import {detectWebXrSupport, startWebXrEngine} from './webxrEngine'
import {loadXr8Script, startXr8Engine} from './xr8Engine'

export type {CameraStatus, EngineCallbacks, EngineKind, EngineSession} from './types'
export type {WebXrSupport} from './webxrEngine'
export {detectWebXrSupport} from './webxrEngine'

export interface EngineChoice {
  kind: EngineKind
  /** Human-readable justification, surfaced in the ?debug=1 overlay. */
  reason: string
  /** Whether the choice came from the ?engine= override rather than detection. */
  forced: boolean
  support: WebXrSupport
}

/** ?engine=webxr | ?engine=xr8 — forces one implementation for A/B field testing. */
function forcedKind(): EngineKind | null {
  const value = new URLSearchParams(window.location.search).get('engine')
  return value === 'webxr' || value === 'xr8' ? value : null
}

let choicePromise: Promise<EngineChoice> | null = null

/** Resolve (once per page load) which engine this device should run. */
export function getEngineChoice(): Promise<EngineChoice> {
  if (!choicePromise) {
    choicePromise = (async () => {
      const support = await detectWebXrSupport()
      const forced = forcedKind()
      if (forced) {
        return {
          kind: forced,
          forced: true,
          reason: `forced by ?engine=${forced}`,
          support,
        }
      }
      return support.arSupported
        ? {kind: 'webxr' as const, forced: false, reason: 'immersive-ar is supported', support}
        : {
            kind: 'xr8' as const,
            forced: false,
            reason: support.reason ?? 'no WebXR AR',
            support,
          }
    })()
  }
  return choicePromise
}

/**
 * Start fetching whatever the chosen engine needs, before the user taps Start.
 * Called once at app start (main.tsx). On the 8th Wall path this restores the
 * old index.html behaviour of warming xr.js during the landing page; on the
 * WebXR path it deliberately does nothing, so those 6.3 MB are never fetched.
 */
export function preloadEngine(): void {
  void getEngineChoice()
    .then((choice) => {
      if (choice.kind === 'xr8') loadXr8Script()
    })
    .catch(() => loadXr8Script()) // detection failed → assume the universal path
}

/**
 * Boot the AR engine on a canvas. Resolves once a three.js scene + camera exist
 * and the camera pose is being driven.
 *
 * Must be called from a user gesture (WebXR's requestSession requires transient
 * user activation; iOS's motion permission requires it too).
 */
export async function startEngine(
  canvas: HTMLCanvasElement,
  callbacks: EngineCallbacks,
): Promise<EngineSession> {
  const choice = await getEngineChoice()
  if (choice.kind === 'webxr') {
    try {
      return await startWebXrEngine(canvas, callbacks)
    } catch (err) {
      // A forced override must fail loudly — silently running the other engine
      // would invalidate an A/B field test. Otherwise, a device that claimed
      // immersive-ar support but could not deliver still gets a working AR
      // experience via the camera pipeline.
      if (choice.forced) throw err
      console.warn('[engine] WebXR failed, falling back to the 8th Wall engine', err)
      return await startXr8Engine(canvas, callbacks)
    }
  }
  return startXr8Engine(canvas, callbacks)
}

/**
 * iOS requires DeviceMotion/Orientation permission from a user gesture. Call this
 * inside the Start-button click handler, before starting the engine. Resolves true
 * if granted or not required (Android/desktop).
 */
export async function ensureMotionPermission(): Promise<boolean> {
  const DOE = DeviceOrientationEvent as unknown as {requestPermission?: () => Promise<string>}
  const DME = DeviceMotionEvent as unknown as {requestPermission?: () => Promise<string>}
  try {
    if (typeof DME?.requestPermission === 'function') {
      const motion = await DME.requestPermission()
      if (motion !== 'granted') return false
    }
    if (typeof DOE?.requestPermission === 'function') {
      const orientation = await DOE.requestPermission()
      if (orientation !== 'granted') return false
    }
    return true
  } catch {
    return false
  }
}

/** True when this device could plausibly run camera AR (mobile + secure context). */
export function isLikelyARCapable(): boolean {
  const mobile = /android|iphone|ipad|ipod/i.test(navigator.userAgent)
  return mobile && window.isSecureContext && !!navigator.mediaDevices?.getUserMedia
}
